<template>
  <div
    class="MeasurementInput"
    :data-direction="direction"
    :data-has-value="modelValue !== null"
  >
    <div
      class="MeasurementInput__header"
      data-testid="header"
      aria-hidden="true"
    >
      <Transition>
        <span
          :key="String(!!modelValue)"
          class="MeasurementInput__headerTitle"
        >{{ formattedHeader || "Swipe to set" }}</span>
      </Transition>
    </div>

    <div
      ref="trackRef"
      class="MeasurementInput__track"
      @pointerdown="handlePointerInteract"
      @pointermove="handlePointerInteract"
      @scroll="handleScroll"
    >
      <div
        v-for="tick in ticks"
        :key="tick"
        class="MeasurementInput__trackTick"
        data-testid="tick"
        :data-size="tick % interval === 0 ? 'large' : 'small'"
        :data-is-active="tick === modelValue"
      >
        <span
          v-if="tick % interval === 0"
          class="MeasurementInput__trackTickLabel"
          data-testid="tickLabel"
        >
          <slot
            name="tick"
            :tick="tick"
          />
        </span>
      </div>
    </div>

    <input
      v-model.number="modelValue"
      class="MeasurementInput__input"
      type="range"
      step="1"
      :min="minValue"
      :max="maxValue"
      :aria-valuetext="formattedHeader ?? 'Not set'"
    >

    <button
      class="MeasurementInput__resetButton"
      type="button"
      aria-label="Reset height"
      data-testid="resetButton"
      @click="handleResetButtonClick"
    >
      <Icon
        class="MeasurementInput__resetButtonIcon"
        name="circleX"
        size="large"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import haptic from "@/lib/utils/platform/haptic";
import { ImpactStyle } from "@capacitor/haptics";
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import Icon from "./Icon.vue";

const modelValue = defineModel<number | null>({
  required: true,
});

const {
  label,
  minValue,
  maxValue,
  interval,
} = defineProps<{
  label?: string,
  minValue: number,
  maxValue: number,
  interval: number,
}>();

const resizeObserver = new ResizeObserver(handleResize);
const trackRef = useTemplateRef<HTMLElement>("trackRef");
const gap = 8;
const margin = gap;
const tickCount = maxValue - minValue + 1;
const ticks = Array.from({ length: tickCount }, (_, i) => minValue + i);

const width = ref(0);
const scrollLeft = ref(0);
const scrollCenter = ref(0);
const scrollWidth = ref(0);
const hasInteracted = ref(false);
const direction = ref<"left" | "right">("left");

onMounted(() => {
  if (!trackRef.value) return;

  updateScrollCenter();
  resizeObserver.observe(trackRef.value);
  handleResize();
  trackRef.value.scrollLeft = modelValue.value !== null
    ? (modelValue.value - minValue) * (scrollWidth.value - width.value) / (maxValue - minValue)
    : scrollCenter.value;
});

onBeforeUnmount(() => {
  if (!trackRef.value) return;
  resizeObserver.disconnect();
});

const formattedHeader = computed(() => {
  if (modelValue.value === null) return;

  return label;
});

const centerValue = computed(() => {
  return Math.round((minValue + maxValue) / 2);
});

function updateScrollCenter () {
  if (!trackRef.value) return;

  scrollCenter.value = (trackRef.value.scrollWidth - trackRef.value.clientWidth) / 2;
}

function handlePointerInteract () {
  hasInteracted.value = true;
}

function handleResetButtonClick () {
  direction.value = modelValue.value && modelValue.value > centerValue.value ? "right" : "left";
  modelValue.value = null;
  hasInteracted.value = false;
  haptic();

  trackRef.value?.scrollTo({
    behavior: "smooth",
    left: scrollCenter.value,
  });
}

function handleResize () {
  if (!trackRef.value) return;

  const {
    width: trackWidth,
  } = trackRef.value.getBoundingClientRect();

  width.value = trackWidth;
  scrollWidth.value = trackRef.value.scrollWidth;
  updateScrollCenter();

  if (modelValue.value !== null) {
    handleScroll();
  }
}

function handleScroll () {
  if (!trackRef.value || !hasInteracted.value) return;

  scrollLeft.value = trackRef.value.scrollLeft;
  direction.value = scrollLeft.value > scrollCenter.value ? "left" : "right";

  const progress = (scrollLeft.value - margin) / (scrollWidth.value - margin - width.value);
  const value = Math.min(maxValue, Math.max(minValue, minValue + Math.round(progress * tickCount)));

  if (value !== modelValue.value) haptic(value % 12 === 0 ? ImpactStyle.Heavy : ImpactStyle.Light);
  modelValue.value = value;
}
</script>

<style lang="scss" scoped>
.MeasurementInput {
  --highlightWidth: 3px;
  --outlineWidth: 2px;
  --offsetWidth: calc(var(--highlightWidth) + var(--outlineWidth));
  --borderRadius: calc(6px + var(--offsetWidth));
  --outlineColorDisabled: var(--themeDarkTransparent500);
  --backgroundColorDisabled: var(--themeLightTransparent600);
  --textColorDisabled: var(--themeDarkTransparent500);
  --placeholderColorDisabled: var(--themeDarkTransparent400);
  --highlightColorDisabled: var(--themeLightTransparent400);
  --transitionDuration: 350ms;
  position: relative;
  isolation: isolate;

  display: grid;
  padding: 20px 0 40px;
  overflow: hidden;

  border-radius: var(--borderRadius);
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);
  box-shadow: 0 0 0 var(--highlightWidth) transparent inset;

  transition: 350ms;
  transition-property: color, box-shadow, background-color;

  &::after {
    position: absolute;
    inset: 0;

    border-radius: inherit;
    outline: var(--outlineWidth) solid var(--themeColorNeutral900);
    outline-offset: calc(-1 * var(--offsetWidth));

    transition: outline-color var(--transitionDuration);

    pointer-events: none;
    content: "";
  }

  [data-theme="light"] & {
    --highlightWidth: 0px;
    --outlineColorDisabled: var(--themeColorNeutral400);
    box-shadow: 0 0 0 var(--highlightWidth) transparent;
  }

  &:has(:disabled) {
    background-color: var(--backgroundColorDisabled);
    color: var(--textColorDisabled);

    &::after {
      outline-color: var(--outlineColorDisabled);
    }
  }

  &:focus-within {
    box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorSecondary) inset;

    [data-theme="light"] & {
      box-shadow: 0 0 0 3px var(--themeColorSecondary);
    }
  }
}

.MeasurementInput__header {
  position: relative;

  display: grid;
  place-items: center;
  margin-bottom: 44px;

  font: var(--fontWeightNormal) 18px/1 var(--fontFamilyMono);
}

.MeasurementInput__headerTitle {
  display: block;
  transition: 150ms;
  transition-property: opacity, transform;

  &.v-leave-active {
    position: absolute;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .MeasurementInput[data-direction='right'] &.v-enter-from,
  .MeasurementInput[data-direction='left'] &.v-leave-to {
    transform: translateX(-24px);
  }

  .MeasurementInput[data-direction='right'] &.v-leave-to,
  .MeasurementInput[data-direction='left'] &.v-enter-from {
    transform: translateX(24px);
  }
}

.MeasurementInput__track {
  position: absolute;
  inset: 0;

  display: flex;
  gap: 8px;
  align-items: end;
  width: 100%;
  height: 100%;
  padding: 0 calc(50% - 1px) 40px;
  overflow-x: auto;

  outline: none;

  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  &::before,
  &::after {
    content: "";
  }
}

.MeasurementInput__trackTick {
  position: relative;

  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: end;
  align-items: center;

  scroll-snap-align: center;

  // TODO: disable tab focus

  &::before {
    position: absolute;
    inset: 0;

    background-color: var(--themeDarkTransparent150);

    content: "";
  }

  &[data-size="small"] {
    transform: translate(-1px, -8px);
    width: 2px;
    height: 16px;
  }

  &[data-size="large"] {
    transform: translateX(-2px);
    width: 3px;
    height: 24px;
  }

  &[data-is-active="true"] {
    &::before {
      transform: scaleY(1.25);
      transform-origin: bottom center;
      background-color: var(--colorOrange);
    }
  }
}

.MeasurementInput__trackTickLabel {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);

  font: var(--fontWeightNormal) 16px/1 var(--fontFamilyMono);
  white-space: nowrap;

  transition: 150ms opacity;

  .MeasurementInput[data-has-value='false'] & {
    opacity: 0.3;
  }
}

.MeasurementInput__input {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;

  opacity: 0;
}

.MeasurementInput__resetButton {
  --buttonInset: calc(8px + var(--offsetWidth));
  --buttonSize: 44px;
  --iconSize: 20px;
  position: absolute;
  top: calc(var(--buttonInset) - (var(--buttonSize) - var(--iconSize)) / 2);
  right: calc(var(--buttonInset) - (var(--buttonSize) - var(--iconSize)) / 2);

  display: grid;
  place-items: center;
  width: var(--buttonSize);
  height: var(--buttonSize);

  outline: none;
  color: var(--themeColorNeutral300);

  .MeasurementInput[data-has-value='false'] & {
    display: none;
  }
}

.MeasurementInput__resetButtonIcon {
  .MeasurementInput__resetButton:focus-visible & {
    border-radius: 50%;
    outline: var(--outlineWidth) solid var(--themeColorSecondary);
    outline-offset: var(--outlineWidth);
  }
}
</style>
